import { SquarePen, X } from "lucide-react"
import { observer } from "mobx-react"
import { useEffect, useRef } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { TaskCreateWorkspacePicker } from "../components/TaskCreateWorkspacePicker"
import { Z_INDEX } from "../constants"
import { useMediaQuery } from "../hooks/useMediaQuery"
import { useCodeStore } from "../store/context"
import { TaskCreateDraftsMenu, TaskCreatePage } from "./TaskCreatePage"

const FOCUSABLE_SELECTOR = [
    "button:not([disabled])",
    "a[href]",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[contenteditable='true']",
    "[contenteditable='plaintext-only']",
    "[tabindex]:not([tabindex='-1'])",
].join(",")

export const TaskCreateOverlay = observer(
    ({ workspaceId, onDismiss, onWorkspaceSelect }: { workspaceId: string; onDismiss: () => void; onWorkspaceSelect: (workspaceId: string) => void }) => {
        const codeStore = useCodeStore()
        const compact = useMediaQuery("(max-width: 900px)")
        const dialogRef = useRef<HTMLDivElement>(null)
        const repo = codeStore.repos.getRepo(workspaceId)

        useHotkeys(
            "esc",
            (event) => {
                event.preventDefault()
                event.stopPropagation()
                onDismiss()
            },
            {
                enableOnContentEditable: true,
                enableOnFormTags: ["INPUT", "TEXTAREA", "SELECT"],
            },
            [onDismiss]
        )

        useEffect(() => {
            const background = document.querySelector<HTMLElement>("[data-code-route-background='true'], [data-code-app-shell='true']")
            const wasInert = background?.inert ?? false
            if (background) background.inert = true

            const handleTabKey = (event: KeyboardEvent) => {
                if (event.key !== "Tab") return
                const dialog = dialogRef.current
                if (!dialog || !(document.activeElement instanceof HTMLElement) || !dialog.contains(document.activeElement)) return

                const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
                if (focusable.length === 0) {
                    event.preventDefault()
                    dialog.focus()
                    return
                }

                const first = focusable[0]
                const last = focusable[focusable.length - 1]
                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault()
                    last.focus()
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault()
                    first.focus()
                }
            }

            document.addEventListener("keydown", handleTabKey)
            return () => {
                document.removeEventListener("keydown", handleTabKey)
                if (background) background.inert = wasInert
            }
        }, [])

        const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
            if (event.target === event.currentTarget) onDismiss()
        }

        const handleWorkspaceSelect = (nextWorkspaceId: string) => {
            codeStore.smartEditors.swapDrafts("task-create", workspaceId, nextWorkspaceId)
            onWorkspaceSelect(nextWorkspaceId)
        }

        return (
            <div
                className="fixed inset-0 flex items-start justify-center bg-black/50 p-4 max-[900px]:items-stretch max-[900px]:p-0"
                style={{
                    zIndex: Z_INDEX.TASK_CREATE_MODAL,
                    backdropFilter: "blur(5px)",
                    WebkitBackdropFilter: "blur(5px)",
                    paddingTop: compact ? "0" : "max(min(80px, 12dvh), 1rem)",
                }}
                onClick={handleBackdropClick}
                data-task-create-backdrop="true"
            >
                <div
                    ref={dialogRef}
                    className="flex h-[min(860px,calc(100dvh-6rem))] w-full max-w-6xl flex-col overflow-hidden border border-border bg-base-100 shadow-2xl outline-none max-[900px]:h-full max-[900px]:max-w-none max-[900px]:border-0"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="new-task-modal-title"
                    tabIndex={-1}
                    data-task-create-overlay="true"
                >
                    <div
                        className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-3 max-[900px]:pb-2"
                        style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}
                    >
                        <div className="flex min-w-0 items-center gap-2">
                            <SquarePen size={18} className="shrink-0 text-muted" />
                            <h1 id="new-task-modal-title" className="truncate text-lg font-semibold text-base-content">
                                New task{repo ? ` in ${repo.name}` : ""}
                            </h1>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                            {repo && <TaskCreateDraftsMenu workspaceId={workspaceId} />}
                            <button
                                type="button"
                                className="btn flex h-8 w-8 items-center justify-center text-muted transition-colors hover:bg-base-200 hover:text-base-content max-[900px]:h-11 max-[900px]:w-11"
                                onClick={onDismiss}
                                title="Close"
                                aria-label="Close new task"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>

                    <TaskCreateWorkspacePicker workspaceId={workspaceId} onSelect={handleWorkspaceSelect} />

                    <div className="min-h-0 flex-1">
                        {repo ? (
                            <TaskCreatePage workspaceId={workspaceId} repo={repo} />
                        ) : (
                            <div className="flex h-full flex-col items-center justify-center px-6 text-center text-muted">
                                <SquarePen size="2rem" className="mb-3 opacity-40" />
                                <div className="text-sm">This project is no longer available. Choose another project above.</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }
)
